import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';
import type { ApprovalItemKind } from '@/lib/approvals/kinds';
import {
  type ApprovalProgress,
  type ApprovalRequestState,
  computeProgress,
  latestDecisionByItem,
  requestState,
} from '@/lib/approvals/progress';
import {
  type ApprovalSnapshot,
  carryPillarFraming,
  hashSnapshot,
  sameSnapshotWording,
  snapshotFromArticle,
  snapshotFromPlanSlot,
  snapshotFromPost,
  snapshotFromTask,
} from '@/lib/approvals/snapshot';
import { defaultExpiry, generateShareToken, hashShareToken } from '@/lib/approvals/token';
import { resolveOwnerId } from '@/lib/identity/resolveOwnerId';
import { hashContent } from '@/lib/knowledge/extract-document';
import { candidatePostTitle } from '@/lib/posts/post-title';

type ItemRow = Database['public']['Tables']['approval_items']['Row'];
type DecisionRow = Database['public']['Tables']['approval_decisions']['Row'];
type ReviewerRow = Database['public']['Tables']['approval_reviewers']['Row'];

export interface ApprovalReviewerSummary {
  id: string;
  name: string;
  email: string | null;
  expiresAt: string;
  revokedAt: string | null;
  lastOpenedAt: string | null;
  openCount: number;
}

export interface ApprovalRequestSummary {
  id: string;
  title: string;
  note: string | null;
  status: string;
  state: ApprovalRequestState;
  dueOn: string | null;
  reachEventId: string | null;
  reachEventName: string | null;
  projectId: string | null;
  progress: ApprovalProgress;
  reviewers: ApprovalReviewerSummary[];
  itemIds: string[];
  createdAt: string;
}

export interface ApprovalRequestDetail extends ApprovalRequestSummary {
  items: ItemRow[];
  decisions: DecisionRow[];
  /** Item ids whose live source row no longer matches what was approved. */
  changedSinceDecision: string[];
}

/**
 * ONE THING A CLIENT SAID, WITH THEIR NAME ON IT.
 *
 * 🔴 THIS IS THE HALF OF THE ROOM THE OPERATOR COULD NOT SEE. Every operator
 * surface reported decisions as an AGGREGATE — "8/8", "Needs changes" — and
 * nowhere in the app could Justin read *what* his client actually said.
 * Measured on the real ITC Tax workspace 2026-09-09: Mark's eight decisions
 * include five approvals, one declined prompt, one answered input, and one
 * change request carrying the sentence _"Use the Permian midstream example
 * instead — that one is public."_ That sentence is the single most valuable
 * row on this workspace and it was reachable only by SQL.
 *
 * Grain is ONE DECISION, not one batch and not one person. A person is a
 * grouping of these; a batch is a different grouping of the same rows. Storing
 * the finest grain and letting the surface group it is why this does not need
 * a second query when the page decides to sort by person instead of by date.
 */
export interface ApprovalDecisionRecord {
  id: string;
  verdict: string;
  itemKind: string;
  /** The reviewer's NAME only. Ids never leave this layer for a client-facing
   * surface, and the desk's own forbidden-field test asserts the same rule on
   * the payload side — see `src/lib/desk/types.ts`. */
  who: string;
  itemTitle: string | null;
  comment: string | null;
  responseText: string | null;
  requestTitle: string | null;
  createdAt: string;
}

/**
 * One row the operator can put into a review. Assembled from the event's plan
 * so the packet the client receives is the run-of-show they already read,
 * not a flat list of posts stripped of its dates.
 */
export interface ApprovalCandidate {
  key: string;
  itemKind: ApprovalItemKind;
  title: string;
  body: string | null;
  occursOn: string | null;
  occursLabel: string | null;
  socialPostId: string | null;
  projectTaskId: string | null;
  snapshot: ApprovalSnapshot;
  /** False when the slot has no drafted artifact yet — nothing to judge. */
  hasDraft: boolean;
}

/**
 * A candidate whose last client decision was "changes requested" and whose
 * wording is unchanged since — sending it again asks the client to re-decide
 * on text that does not contain the change they asked for.
 */
export interface UnchangedResend {
  key: string;
  title: string;
  /** What the client asked for, verbatim, when they left a comment. */
  comment: string | null;
  decidedAt: string;
}

const TASK_CANDIDATE_COLUMNS =
  'id, topic, rationale, cta, summary, platform, scheduled_at, position, artifact_id, artifact_kind, type' as const;

type TaskCandidateRow = Pick<
  Database['public']['Tables']['project_tasks']['Row'],
  | 'id'
  | 'topic'
  | 'rationale'
  | 'cta'
  | 'summary'
  | 'platform'
  | 'scheduled_at'
  | 'position'
  | 'artifact_id'
  | 'artifact_kind'
  | 'type'
>;

export interface NewApprovalItemInput {
  itemKind: ApprovalItemKind;
  title: string;
  body?: string | null;
  occursOn?: string | null;
  occursLabel?: string | null;
  socialPostId?: string | null;
  mediaId?: string | null;
  projectTaskId?: string | null;
  snapshot?: ApprovalSnapshot;
}

export interface CreateApprovalRequestInput {
  workspaceId: string;
  title: string;
  note?: string | null;
  reachEventId?: string | null;
  projectId?: string | null;
  dueOn?: string | null;
  /**
   * Mint a NEW, EXPIRING, batch-scoped reviewer for this one request.
   *
   * 🔴 OMIT THIS TO SEND INTO THE ROOM, WHICH IS ALMOST ALWAYS WHAT YOU WANT.
   * `createRequest` used to mint one unconditionally, so an operator who had
   * already added someone as a permanent desk member and then sent them a
   * batch got a SECOND row for the same human on a second link that dies in 30
   * days — and the prewritten text message pointed at the expiring one. That is
   * how the real ITC workspace accumulated four reviewer rows for roughly two
   * people, with its only engaged reviewer sitting on a token that expires.
   *
   * Nothing has to be wired for a desk member to see a new batch:
   * `resolveReviewContext` already scopes a desk member to EVERY non-draft
   * request in their workspace. Sending into the room is therefore the absence
   * of this field, not a feature.
   */
  reviewerName?: string;
  reviewerEmail?: string | null;
  items: NewApprovalItemInput[];
}

export interface CreatedApprovalRequest {
  requestId: string;
  /** Null when the batch was sent into the room rather than to a new link. */
  reviewerId: string | null;
  /**
   * The plaintext share token, returned EXACTLY ONCE at creation. It is never
   * persisted (only its sha-256 is) and there is no read path that can return
   * it again — losing it means issuing a new link, by design.
   *
   * Null when no reviewer was minted: the batch went into the room, and the
   * room's members already hold permanent links that do not change.
   */
  token: string | null;
  /**
   * How many live people this batch just became visible to through the room.
   * The operator needs this to know whether the send reached anyone at all —
   * a batch sent into a room with no members is addressed to nobody.
   */
  deskMemberCount: number;
}

function toReviewerSummary(row: ReviewerRow): ApprovalReviewerSummary {
  return {
    id: row.id,
    name: row.name,
    email: row.email,
    expiresAt: row.expires_at,
    revokedAt: row.revoked_at,
    lastOpenedAt: row.last_opened_at,
    openCount: row.open_count ?? 0,
  };
}

/**
 * Pulls the written article out of an `ai_messages.parts` array.
 *
 * The tool output is the only durable copy of a generated document — there is
 * no articles table — so this mirrors `useEditorialArticleTrail`'s reading of
 * the same shape. Newest-first, because a topic can be rewritten and the last
 * `tool-generate_document` output is the current article.
 *
 * Defensive throughout, because `parts` is untyped jsonb: a malformed row must
 * yield null and let the caller fall back to the plan blurb, never throw and
 * take the whole candidate list down with it.
 */
function readArticleFromParts(
  parts: unknown,
): { markdown: string | null; title: string | null; documentId: string | null } | null {
  if (!Array.isArray(parts)) return null;
  for (let i = parts.length - 1; i >= 0; i -= 1) {
    const part = parts[i] as { type?: unknown; output?: unknown } | null;
    if (!part || part.type !== 'tool-generate_document') continue;
    const output = part.output as { id?: unknown; markdown?: unknown; title?: unknown } | null;
    if (!output || typeof output.markdown !== 'string' || !output.markdown.trim()) continue;
    return {
      markdown: output.markdown,
      documentId: typeof output.id === 'string' ? output.id : null,
      title: typeof output.title === 'string' ? output.title : null,
    };
  }
  return null;
}

export class ApprovalService {
  /**
   * Every review in a workspace with the counts its row needs. Decisions are
   * fetched in one query across all requests and reduced in memory rather than
   * per-request — a workspace's whole review history is small, and the
   * alternative is an N+1 that grows with every event.
   */
  async listRequests(workspaceId: string): Promise<ApprovalRequestSummary[]> {
    const { data: requests, error } = await supabase
      .from('approval_requests')
      .select('*')
      .eq('workspace_id', workspaceId)
      .order('created_at', { ascending: false });
    if (error) throw new Error(error.message);
    if (!requests || requests.length === 0) return [];

    const requestIds = requests.map((r) => r.id);
    const eventIds = requests
      .map((r) => r.reach_event_id)
      .filter((id): id is string => Boolean(id));

    const [itemsResult, decisionsResult, reviewersResult, eventsResult] = await Promise.all([
      supabase
        .from('approval_items')
        .select('id, request_id, item_kind')
        .in('request_id', requestIds),
      supabase
        .from('approval_decisions')
        .select('id, item_id, request_id, verdict, created_at')
        .in('request_id', requestIds),
      supabase.from('approval_reviewers').select('*').in('request_id', requestIds),
      eventIds.length
        ? supabase.from('reach_events').select('id, name').in('id', eventIds)
        : Promise.resolve({ data: [], error: null }),
    ]);
    if (itemsResult.error) throw new Error(itemsResult.error.message);
    if (decisionsResult.error) throw new Error(decisionsResult.error.message);
    if (reviewersResult.error) throw new Error(reviewersResult.error.message);
    if (eventsResult.error) throw new Error(eventsResult.error.message);

    const eventNameById = new Map((eventsResult.data ?? []).map((e) => [e.id, e.name]));

    return requests.map((request) => {
      const items = (itemsResult.data ?? []).filter((i) => i.request_id === request.id);
      const decisions = (decisionsResult.data ?? []).filter((d) => d.request_id === request.id);
      const progress = computeProgress(items, decisions);
      return {
        id: request.id,
        title: request.title,
        note: request.note,
        status: request.status,
        state: requestState(request.status, progress),
        dueOn: request.due_on,
        reachEventId: request.reach_event_id,
        reachEventName: request.reach_event_id
          ? (eventNameById.get(request.reach_event_id) ?? null)
          : null,
        projectId: request.project_id,
        progress,
        reviewers: (reviewersResult.data ?? [])
          .filter((r) => r.request_id === request.id)
          .map(toReviewerSummary),
        itemIds: items.map((item) => item.id),
        createdAt: request.created_at,
      };
    });
  }

  /**
   * Every decision anyone in this workspace's room has made, newest first.
   *
   * 🔴 SCOPED BY `workspace_id`, NOT BY `request_id`. `listRequests` above joins
   * reviewers with `.in('request_id', requestIds)`, which is the batch-shaped
   * read and is exactly why a ROOM MEMBER renders as "No reviewer yet" — their
   * row carries `request_id IS NULL` by definition. Reading decisions by
   * workspace is what makes this correct for both reviewer models at once, and
   * it stays correct after the legacy per-batch rows are gone.
   *
   * ⚠️ `propagation_status` is deliberately NOT surfaced. The column was added
   * on 2026-09-07 with `DEFAULT 'pending'`, and ITC's eight decisions were made
   * on Sep 4–5, so all eight read "pending" while their write-back demonstrably
   * landed (five tasks sit at `status = 'approved'`). Rendering that column
   * today would tell the operator that eight settled decisions never
   * propagated. It becomes safe to show once rows written after the migration
   * are the norm; until then it is a backfill default wearing a status word.
   */
  async listDecisions(workspaceId: string, limit = 50): Promise<ApprovalDecisionRecord[]> {
    const { data: decisions, error } = await supabase
      .from('approval_decisions')
      .select(
        'id, item_id, request_id, reviewer_id, verdict, item_kind, comment, response_text, created_at',
      )
      .eq('workspace_id', workspaceId)
      .order('created_at', { ascending: false })
      .limit(limit);
    if (error) throw new Error(error.message);
    if (!decisions || decisions.length === 0) return [];

    const itemIds = decisions.map((d) => d.item_id).filter((id): id is string => Boolean(id));
    const reviewerIds = decisions
      .map((d) => d.reviewer_id)
      .filter((id): id is string => Boolean(id));
    const requestIds = decisions.map((d) => d.request_id).filter((id): id is string => Boolean(id));

    const [itemsResult, reviewersResult, requestsResult] = await Promise.all([
      itemIds.length
        ? supabase.from('approval_items').select('id, title').in('id', itemIds)
        : Promise.resolve({ data: [], error: null }),
      reviewerIds.length
        ? supabase.from('approval_reviewers').select('id, name').in('id', reviewerIds)
        : Promise.resolve({ data: [], error: null }),
      requestIds.length
        ? supabase.from('approval_requests').select('id, title').in('id', requestIds)
        : Promise.resolve({ data: [], error: null }),
    ]);
    if (itemsResult.error) throw new Error(itemsResult.error.message);
    if (reviewersResult.error) throw new Error(reviewersResult.error.message);
    if (requestsResult.error) throw new Error(requestsResult.error.message);

    const titleById = new Map((itemsResult.data ?? []).map((i) => [i.id, i.title]));
    const nameById = new Map((reviewersResult.data ?? []).map((r) => [r.id, r.name]));
    const requestTitleById = new Map((requestsResult.data ?? []).map((r) => [r.id, r.title]));

    return decisions.map((decision) => ({
      id: decision.id,
      verdict: decision.verdict,
      itemKind: decision.item_kind,
      /*
       * "Someone" rather than a blank or an id. A decision with an unresolvable
       * reviewer is a real row a real person made; printing nothing there would
       * read as a system action, which is the one thing it is not.
       */
      who: (decision.reviewer_id ? nameById.get(decision.reviewer_id) : null) ?? 'Someone',
      itemTitle: (decision.item_id ? titleById.get(decision.item_id) : null) ?? null,
      comment: decision.comment,
      responseText: decision.response_text,
      requestTitle:
        (decision.request_id ? requestTitleById.get(decision.request_id) : null) ?? null,
      createdAt: decision.created_at,
    }));
  }

  async getRequest(requestId: string): Promise<ApprovalRequestDetail | null> {
    const { data: request, error } = await supabase
      .from('approval_requests')
      .select('*')
      .eq('id', requestId)
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!request) return null;

    const [itemsResult, decisionsResult, reviewersResult] = await Promise.all([
      supabase
        .from('approval_items')
        .select('*')
        .eq('request_id', requestId)
        .order('position', { ascending: true }),
      supabase
        .from('approval_decisions')
        .select('*')
        .eq('request_id', requestId)
        .order('created_at', { ascending: true }),
      supabase.from('approval_reviewers').select('*').eq('request_id', requestId),
    ]);
    if (itemsResult.error) throw new Error(itemsResult.error.message);
    if (decisionsResult.error) throw new Error(decisionsResult.error.message);
    if (reviewersResult.error) throw new Error(reviewersResult.error.message);

    const items = itemsResult.data ?? [];
    const decisions = decisionsResult.data ?? [];
    const progress = computeProgress(items, decisions);

    let eventName: string | null = null;
    if (request.reach_event_id) {
      const { data: event } = await supabase
        .from('reach_events')
        .select('name')
        .eq('id', request.reach_event_id)
        .maybeSingle();
      eventName = event?.name ?? null;
    }

    return {
      id: request.id,
      title: request.title,
      note: request.note,
      status: request.status,
      state: requestState(request.status, progress),
      dueOn: request.due_on,
      reachEventId: request.reach_event_id,
      reachEventName: eventName,
      projectId: request.project_id,
      progress,
      reviewers: (reviewersResult.data ?? []).map(toReviewerSummary),
      itemIds: items.map((item) => item.id),
      createdAt: request.created_at,
      items,
      decisions,
      changedSinceDecision: await this.findChangedSinceDecision(items, decisions),
    };
  }

  /**
   * Items whose live source row no longer hashes to what was decided on. The
   * reviewer is never shown a stale item — they see the snapshot — so this
   * state belongs to the OPERATOR: it says "you edited this after they
   * approved it, and their approval no longer covers what is there now."
   */
  private async findChangedSinceDecision(
    items: ItemRow[],
    decisions: DecisionRow[],
  ): Promise<string[]> {
    const latest = latestDecisionByItem(decisions);
    const decidedPostItems = items.filter((item) => item.social_post_id && latest.has(item.id));
    const decidedTaskItems = items.filter((item) => item.project_task_id && latest.has(item.id));
    if (!decidedPostItems.length && !decidedTaskItems.length) return [];

    const postIds = decidedPostItems
      .map((item) => item.social_post_id)
      .filter((id): id is string => Boolean(id));
    const { data: posts, error } = postIds.length
      ? await supabase
          .from('social_posts')
          .select('id, content, title, platforms, media_urls, media_type, scheduled_at')
          .in('id', postIds)
      : { data: [], error: null };
    if (error) throw new Error(error.message);

    const postById = new Map((posts ?? []).map((post) => [post.id, post]));
    const changed: string[] = [];
    for (const item of decidedPostItems) {
      const post = postById.get(item.social_post_id as string);
      if (!post) continue;
      const liveHash = await hashSnapshot(
        carryPillarFraming(snapshotFromPost(post), item.snapshot as ApprovalSnapshot | null),
      );
      const decidedHash = latest.get(item.id)?.item_snapshot_hash;
      if (decidedHash && liveHash !== decidedHash) changed.push(item.id);
    }
    if (decidedTaskItems.length) {
      const tasks = await supabase
        .from('project_tasks')
        .select('id, topic, rationale, cta, platform, scheduled_at, artifact_id, artifact_kind')
        .in(
          'id',
          decidedTaskItems.map((item) => item.project_task_id!),
        );
      if (tasks.error) throw new Error(tasks.error.message);
      const messageIds = (tasks.data ?? [])
        .filter((task) => task.artifact_kind === 'document' && task.artifact_id)
        .map((task) => task.artifact_id!);
      const messages = messageIds.length
        ? await supabase.from('ai_messages').select('id, parts').in('id', messageIds)
        : { data: [], error: null };
      if (messages.error) throw new Error(messages.error.message);
      const byMessage = new Map(
        (messages.data ?? []).map((message) => [message.id, readArticleFromParts(message.parts)]),
      );
      for (const item of decidedTaskItems) {
        const task = tasks.data?.find((candidate) => candidate.id === item.project_task_id);
        if (!task) {
          changed.push(item.id);
          continue;
        }
        const frozen = (item.snapshot ?? {}) as ApprovalSnapshot;
        const article =
          task.artifact_kind === 'document' && task.artifact_id
            ? byMessage.get(task.artifact_id)
            : null;
        if (task.artifact_kind === 'document') {
          if (!article?.markdown || !article.documentId || !frozen.articleRef) {
            changed.push(item.id);
            continue;
          }
          const live = carryPillarFraming(
            snapshotFromArticle({
              markdown: article.markdown,
              title: article.title,
              scheduled_at: task.scheduled_at,
              articleRef: {
                messageId: task.artifact_id!,
                documentId: article.documentId,
                contentHash: await hashContent(article.markdown),
              },
            }),
            frozen,
          );
          if ((await hashSnapshot(live)) !== latest.get(item.id)?.item_snapshot_hash)
            changed.push(item.id);
        } else if (frozen.sourceKind === 'article') {
          changed.push(item.id);
        } else if (
          !task.artifact_id &&
          // Editorial-sheet sends froze an "Audience:" blurb, a different
          // shape from a plan slot (older ones carry no `sourceKind`, only
          // pillar framing); comparing one to a plan slot would flag every
          // such item. They are skipped, exactly as before.
          frozen.sourceKind !== 'proposal' &&
          !(frozen.pillarId && /^(Audience|Reference): /.test(frozen.body ?? '')) &&
          (await hashSnapshot(carryPillarFraming(snapshotFromPlanSlot(task), frozen))) !==
            latest.get(item.id)?.item_snapshot_hash
        ) {
          changed.push(item.id);
        }
      }
    }
    return changed;
  }

  /**
   * The event's plan, turned into proposed review items. Every content slot
   * becomes a candidate: a drafted one is judged, an undrafted one is offered
   * as an input request, because "client POV post or saved draft" is a row the
   * operator's own sheet carries and a review that silently drops it recreates
   * the gap this whole surface exists to close.
   */
  async listCandidatesForEvent(
    workspaceId: string,
    reachEventId: string,
  ): Promise<{ projectId: string | null; candidates: ApprovalCandidate[] }> {
    const { data: source, error: sourceError } = await supabase
      .from('project_sources')
      .select('project_id')
      .eq('workspace_id', workspaceId)
      .eq('reach_event_id', reachEventId)
      .eq('source_kind', 'reach_event')
      .is('revoked_at', null)
      .maybeSingle();
    if (sourceError) throw new Error(sourceError.message);
    if (!source) return { projectId: null, candidates: [] };

    const { data: slots, error: slotsError } = await supabase
      .from('project_tasks')
      .select(TASK_CANDIDATE_COLUMNS)
      .eq('project_id', source.project_id)
      .order('scheduled_at', { ascending: true, nullsFirst: false })
      .order('position', { ascending: true });
    if (slotsError) throw new Error(slotsError.message);

    return {
      projectId: source.project_id,
      candidates: await this.candidatesFromTaskRows(slots ?? []),
    };
  }

  /**
   * Candidates for an explicit set of tasks — the editorial programme's send
   * path. Same builder as `listCandidatesForEvent` (`candidatesFromTaskRows`),
   * so a topic sent from the programme view carries exactly the content it
   * carried when it was sent from its event, with the pillar framing added by
   * the caller on top.
   *
   * Returned in the order the ids were given. Ids that no longer resolve to a
   * task are simply absent.
   */
  async listCandidatesForTasks(taskIds: string[]): Promise<ApprovalCandidate[]> {
    const unique = Array.from(new Set(taskIds));
    if (unique.length === 0) return [];
    const rows: TaskCandidateRow[] = [];
    // PostgREST puts `.in()` in the URL; a 500-topic workspace would overflow it.
    for (let start = 0; start < unique.length; start += 100) {
      const { data, error } = await supabase
        .from('project_tasks')
        .select(TASK_CANDIDATE_COLUMNS)
        .in('id', unique.slice(start, start + 100));
      if (error) throw new Error(error.message);
      rows.push(...(data ?? []));
    }
    const order = new Map(unique.map((id, index) => [id, index]));
    rows.sort((a, b) => (order.get(a.id) ?? 0) - (order.get(b.id) ?? 0));
    return this.candidatesFromTaskRows(rows);
  }

  /**
   * THE ONE PLACE A `project_tasks` ROW BECOMES A REVIEW CANDIDATE.
   * Resolves the task's drafted post / written article, then hands everything to
   * `snapshotFromTask`.
   */
  private async candidatesFromTaskRows(slots: TaskCandidateRow[]): Promise<ApprovalCandidate[]> {
    const artifactIds = slots
      .map((slot) => slot.artifact_id)
      .filter((id): id is string => Boolean(id));
    const { data: posts, error: postsError } = artifactIds.length
      ? await supabase
          .from('social_posts')
          .select('id, content, title, platforms, media_urls, media_type, scheduled_at')
          .in('id', artifactIds)
      : { data: [], error: null };
    if (postsError) throw new Error(postsError.message);
    const postById = new Map((posts ?? []).map((post) => [post.id, post]));

    /*
     * 🔴 AN ARTICLE'S ARTIFACT IS NOT A POST, AND MISSING THAT SHIPPED A LIE.
     *
     * `artifact_kind = 'document'` means the article has been WRITTEN, and its
     * `artifact_id` is an `ai_messages.id` — the tool output is the only
     * durable copy (see `useEditorialArticleTrail`). A lookup that only
     * resolved posts missed every article, fell through to the plan-slot
     * branch, and sent the client the TOPIC-STAGE blurb.
     *
     * On the real ITC Tax workspace that was 11 finished articles rendered as
     * cards labelled "Article" whose entire body read
     * "Audience: Tax Director; CFO; Data Center Asset Manager".
     */
    const documentIds = slots
      .filter((slot) => slot.artifact_kind === 'document' && slot.artifact_id)
      .map((slot) => slot.artifact_id as string);
    const articleById = new Map<
      string,
      { markdown: string | null; title: string | null; documentId: string | null }
    >();
    if (documentIds.length > 0) {
      const { data: messages } = await supabase
        .from('ai_messages')
        .select('id, parts')
        .in('id', documentIds);
      for (const row of messages ?? []) {
        const article = readArticleFromParts(row.parts);
        if (article) articleById.set(row.id as string, article);
      }
    }

    const candidates: ApprovalCandidate[] = [];
    for (const slot of slots) {
      const article =
        slot.artifact_kind === 'document' && slot.artifact_id
          ? articleById.get(slot.artifact_id)
          : undefined;
      const post =
        slot.artifact_kind !== 'document' && slot.artifact_id
          ? postById.get(slot.artifact_id)
          : undefined;
      const occursOn = slot.scheduled_at ? slot.scheduled_at.slice(0, 10) : null;

      // The article branch comes FIRST: a slot with a document artifact is a
      // written piece, and falling through to the plan branch is the defect.
      if (article?.markdown) {
        candidates.push({
          key: slot.id,
          itemKind: 'plan',
          title: slot.topic?.trim() || article.title?.trim() || 'Article',
          body: null,
          occursOn,
          occursLabel: null,
          socialPostId: null,
          projectTaskId: slot.id,
          snapshot: snapshotFromTask(slot, {
            article: {
              ...(article.documentId && slot.artifact_id
                ? {
                    articleRef: {
                      messageId: slot.artifact_id,
                      documentId: article.documentId,
                      contentHash: await hashContent(article.markdown),
                    },
                  }
                : {}),
              markdown: article.markdown,
              title: article.title,
              scheduled_at: slot.scheduled_at,
            },
          }),
          hasDraft: true,
        });
        continue;
      }

      if (post) {
        candidates.push({
          key: slot.id,
          itemKind: 'post',
          title: slot.topic?.trim() || 'Post',
          body: null,
          occursOn,
          occursLabel: null,
          socialPostId: post.id,
          projectTaskId: slot.id,
          snapshot: snapshotFromTask(slot, { post }),
          hasDraft: true,
        });
      } else {
        candidates.push({
          key: slot.id,
          itemKind: 'plan',
          title: slot.topic?.trim() || 'Planned post',
          body: slot.rationale?.trim() || null,
          occursOn,
          occursLabel: null,
          socialPostId: null,
          projectTaskId: slot.id,
          snapshot: snapshotFromTask(slot),
          hasDraft: false,
        });
      }
    }
    return candidates;
  }

  /**
   * Of the candidates about to be sent, those whose last client decision was
   * "changes requested" and whose wording is IDENTICAL to what was decided —
   * a re-send that cannot have made the requested change.
   *
   * Uses `sameSnapshotWording`, the same comparison the desk dedupe uses to
   * decide whether a decision still applies, so the warning and the desk can
   * never disagree. Pillar framing, platform and schedule are ignored.
   */
  async findUnchangedResends(
    workspaceId: string,
    candidates: ApprovalCandidate[],
  ): Promise<UnchangedResend[]> {
    const byTask = new Map(
      candidates
        .filter((candidate) => candidate.projectTaskId)
        .map((candidate) => [candidate.projectTaskId as string, candidate]),
    );
    if (byTask.size === 0) return [];

    const items: Pick<ItemRow, 'id' | 'project_task_id' | 'title' | 'snapshot'>[] = [];
    const taskIds = Array.from(byTask.keys());
    for (let start = 0; start < taskIds.length; start += 100) {
      const { data, error } = await supabase
        .from('approval_items')
        .select('id, project_task_id, title, snapshot')
        .eq('workspace_id', workspaceId)
        .in('project_task_id', taskIds.slice(start, start + 100));
      if (error) throw new Error(error.message);
      items.push(...(data ?? []));
    }
    if (items.length === 0) return [];

    const decisions: Pick<DecisionRow, 'item_id' | 'verdict' | 'comment' | 'created_at'>[] = [];
    const itemIds = items.map((item) => item.id);
    for (let start = 0; start < itemIds.length; start += 100) {
      const { data, error } = await supabase
        .from('approval_decisions')
        .select('item_id, verdict, comment, created_at')
        .in('item_id', itemIds.slice(start, start + 100));
      if (error) throw new Error(error.message);
      decisions.push(...(data ?? []));
    }

    const itemById = new Map(items.map((item) => [item.id, item]));
    const latestByTask = new Map<string, (typeof decisions)[number]>();
    for (const decision of decisions) {
      const taskId = itemById.get(decision.item_id)?.project_task_id;
      if (!taskId) continue;
      const held = latestByTask.get(taskId);
      if (!held || decision.created_at > held.created_at) latestByTask.set(taskId, decision);
    }

    const out: UnchangedResend[] = [];
    for (const [taskId, decision] of latestByTask) {
      if (decision.verdict !== 'changes_requested') continue;
      const decided = itemById.get(decision.item_id);
      const candidate = byTask.get(taskId);
      if (!decided || !candidate) continue;
      if (
        sameSnapshotWording(
          { title: decided.title, snapshot: decided.snapshot as Record<string, unknown> | null },
          { title: candidate.title, snapshot: candidate.snapshot as Record<string, unknown> },
        )
      ) {
        out.push({
          key: candidate.key,
          title: candidate.title,
          comment: decision.comment?.trim() || null,
          decidedAt: decision.created_at,
        });
      }
    }
    return out;
  }

  /**
   * Carry a NOW-WRITTEN article into the snapshot of an item that was sent
   * before it existed.
   *
   * 🔴 WHY THIS EXISTS, MEASURED ON THE REAL ITC TAX DESK (2026-09-09).
   * `listCandidatesForEvent` was fixed to resolve `artifact_kind = 'document'`
   * into a real article body. That fix only governs items created AFTERWARDS.
   * The 72 items already in that room were snapshotted at topic stage and are
   * frozen there by design, so the client kept reading:
   *
   *   "Audience: Tax Director; CFO; Data Center Asset Manager
   *    Reference: Finding the Value in Data Centers presentation"
   *
   * — 113 characters — under a card labelled "Article", inside a dialog sized
   * for a document, while the article itself sat in `ai_messages.parts` at
   * 141,517 characters. The founder's words on seeing it: "is this supposed to
   * be a document reader? this looks like crap". The reader was innocent; it
   * was handed 113 characters.
   *
   * 🔴 THE SAFETY RULE IS THE `decisions === 0` GUARD BELOW, AND IT IS THE
   * WHOLE ARGUMENT FOR THIS METHOD BEING HONEST.
   *
   * The snapshot is frozen so that editing a draft after approval cannot carry
   * the approval to text nobody approved. That guarantee is about a DECISION:
   * with no decision on an item, there is no approval to drift away from, and
   * refreshing is simply showing the reviewer the thing they were always meant
   * to read. With a decision present, refreshing would rewrite what somebody
   * already said yes to — so this refuses, permanently, and the operator's
   * existing "changed since decided" path owns that case instead.
   *
   * The rule lives in the query and in `skippedDecided`, not in this comment,
   * because a rule that must hold goes in code.
   * `__tests__/ApprovalService.refreshStaleArticles.test.ts` asserts a decided
   * item is never rewritten.
   *
   * Collection is split from the write so the operator's banner can COUNT what
   * is stale without writing anything — one query path, so the number on the
   * button can never disagree with what the button then does.
   */
  private async collectStaleArticleItems(workspaceId: string): Promise<{
    refreshable: { id: string; taskId: string }[];
    skippedDecided: number;
    taskById: Map<string, { id: string; artifact_id: string | null; scheduled_at: string | null }>;
  }> {
    const empty = { refreshable: [], skippedDecided: 0, taskById: new Map() };

    const { data: items, error } = await supabase
      .from('approval_items')
      .select('id, snapshot, project_task_id')
      .eq('workspace_id', workspaceId)
      .not('project_task_id', 'is', null);
    if (error) throw new Error(error.message);
    if (!items || items.length === 0) return empty;

    const taskIds = items
      .map((item) => item.project_task_id)
      .filter((id): id is string => Boolean(id));
    const { data: tasks, error: tasksError } = await supabase
      .from('project_tasks')
      .select('id, artifact_id, artifact_kind, scheduled_at')
      .in('id', taskIds)
      .eq('artifact_kind', 'document')
      .not('artifact_id', 'is', null);
    if (tasksError) throw new Error(tasksError.message);
    const taskById = new Map((tasks ?? []).map((task) => [task.id, task]));
    if (taskById.size === 0) return empty;

    // An item that already carries an article snapshot is done; re-hashing it
    // would churn `snapshot_hash` and make every item look changed.
    const stale = items.filter((item) => {
      if (!item.project_task_id || !taskById.has(item.project_task_id)) return false;
      const snapshot = (item.snapshot ?? {}) as ApprovalSnapshot;
      return snapshot.sourceKind !== 'article';
    });
    if (stale.length === 0) return empty;

    // THE GUARD. One query, applied before any write is built.
    const { data: decided, error: decidedError } = await supabase
      .from('approval_decisions')
      .select('item_id')
      .in(
        'item_id',
        stale.map((item) => item.id),
      );
    if (decidedError) throw new Error(decidedError.message);
    const decidedItemIds = new Set((decided ?? []).map((row) => row.item_id));

    const refreshable = stale
      .filter((item) => !decidedItemIds.has(item.id))
      .map((item) => ({ id: item.id, taskId: item.project_task_id as string }));

    return { refreshable, skippedDecided: stale.length - refreshable.length, taskById };
  }

  /** How many items would be rewritten, without rewriting any of them. */
  async countStaleArticleItems(workspaceId: string): Promise<number> {
    const { refreshable } = await this.collectStaleArticleItems(workspaceId);
    return refreshable.length;
  }

  async refreshStaleArticleItems(workspaceId: string): Promise<{
    refreshed: number;
    skippedDecided: number;
    articleMissing: number;
    itemIds: string[];
  }> {
    const { refreshable, skippedDecided, taskById } =
      await this.collectStaleArticleItems(workspaceId);
    if (refreshable.length === 0) {
      return { refreshed: 0, skippedDecided, articleMissing: 0, itemIds: [] };
    }

    const messageIds = Array.from(
      new Set(
        refreshable
          .map((item) => taskById.get(item.taskId)?.artifact_id)
          .filter((id): id is string => Boolean(id)),
      ),
    );
    const { data: messages, error: messagesError } = await supabase
      .from('ai_messages')
      .select('id, parts')
      .in('id', messageIds);
    if (messagesError) throw new Error(messagesError.message);
    const articleById = new Map<
      string,
      { markdown: string | null; title: string | null; documentId: string | null }
    >();
    for (const row of messages ?? []) {
      const article = readArticleFromParts(row.parts);
      if (article) articleById.set(row.id as string, article);
    }

    let refreshed = 0;
    let articleMissing = 0;
    const itemIds: string[] = [];

    for (const item of refreshable) {
      const task = taskById.get(item.taskId);
      const article = task?.artifact_id ? articleById.get(task.artifact_id) : undefined;
      // `artifact_kind` said "document" but the tool output is unreadable or
      // gone. Counted and left alone — the topic blurb is a poor body, but it
      // is a TRUE one, and replacing it with an empty article would be worse.
      if (!article?.markdown?.trim()) {
        articleMissing += 1;
        continue;
      }

      const snapshot = snapshotFromArticle({
        ...(article.documentId && task?.artifact_id
          ? {
              articleRef: {
                messageId: task.artifact_id,
                documentId: article.documentId,
                contentHash: await hashContent(article.markdown),
              },
            }
          : {}),
        markdown: article.markdown,
        title: article.title,
        scheduled_at: task?.scheduled_at ?? null,
      });

      const { error: updateError } = await supabase
        .from('approval_items')
        .update({
          snapshot: snapshot as never,
          snapshot_hash: await hashSnapshot(snapshot),
        })
        .eq('id', item.id);
      if (updateError) throw new Error(updateError.message);

      refreshed += 1;
      itemIds.push(item.id);
    }

    return { refreshed, skippedDecided, articleMissing, itemIds };
  }

  /**
   * The workspace's recent drafted posts, as candidates.
   *
   * Without this a review that is NOT tied to an event could only ever carry
   * questions — the founder explicitly wants non-event reviews ("or not by
   * event too, in general possibly"), and a review with no content in it is
   * not the thing they asked for. Also the path by which a drafted post
   * reaches a client at all when its slot was drafted outside a plan.
   */
  async listDraftCandidates(workspaceId: string, limit = 12): Promise<ApprovalCandidate[]> {
    const { data: posts, error } = await supabase
      .from('social_posts')
      .select('id, content, title, platforms, media_urls, media_type, scheduled_at, created_at')
      .eq('brand_kit_id', workspaceId)
      .eq('status', 'draft')
      .not('content', 'is', null)
      .order('created_at', { ascending: false })
      .limit(limit);
    if (error) throw new Error(error.message);

    return (posts ?? [])
      .filter((post) => (post.content ?? '').trim().length > 0)
      .map((post) => {
        return {
          key: `post:${post.id}`,
          itemKind: 'post' as const,
          title: candidatePostTitle(post),
          body: null,
          occursOn: post.scheduled_at ? post.scheduled_at.slice(0, 10) : null,
          occursLabel: null,
          socialPostId: post.id,
          projectTaskId: null,
          snapshot: snapshotFromPost(post),
          hasDraft: true,
        };
      });
  }

  /**
   * Creates the request, its items and the first reviewer in that order, and
   * hands back the share token once. `owner_id` comes from `resolveOwnerId()`
   * — the single identity seam — never from `auth.uid()` directly.
   */
  async createRequest(input: CreateApprovalRequestInput): Promise<CreatedApprovalRequest> {
    const ownerId = await resolveOwnerId();
    if (!ownerId) throw new Error('Could not resolve the account owner.');

    const {
      data: { user },
    } = await supabase.auth.getUser();

    const { data: request, error: requestError } = await supabase
      .from('approval_requests')
      .insert({
        owner_id: ownerId,
        workspace_id: input.workspaceId,
        reach_event_id: input.reachEventId ?? null,
        project_id: input.projectId ?? null,
        title: input.title.trim(),
        note: input.note?.trim() || null,
        due_on: input.dueOn ?? null,
        status: 'open',
        created_by: user?.id ?? null,
      })
      .select('id')
      .single();
    if (requestError) throw new Error(requestError.message);

    const itemRows = await Promise.all(
      input.items.map(async (item, index) => {
        const snapshot = item.snapshot ?? {};
        return {
          request_id: request.id,
          owner_id: ownerId,
          workspace_id: input.workspaceId,
          position: index,
          item_kind: item.itemKind,
          social_post_id: item.socialPostId ?? null,
          media_id: item.mediaId ?? null,
          project_task_id: item.projectTaskId ?? null,
          occurs_on: item.occursOn ?? null,
          occurs_label: item.occursLabel?.trim() || null,
          title: item.title.trim(),
          body: item.body?.trim() || null,
          snapshot: snapshot as never,
          snapshot_hash: await hashSnapshot(snapshot),
        };
      }),
    );

    if (itemRows.length > 0) {
      const { error: itemsError } = await supabase.from('approval_items').insert(itemRows);
      if (itemsError) throw new Error(itemsError.message);
    }

    // Who this batch reaches through the room. Counted either way, because it
    // is the only answer to "did that send actually go to anybody" — and a
    // batch created into an empty room is the failure this surface exists to
    // prevent, not an edge case.
    const { count: deskMemberCount } = await supabase
      .from('approval_reviewers')
      .select('id', { count: 'exact', head: true })
      .eq('workspace_id', input.workspaceId)
      .is('request_id', null)
      .is('revoked_at', null);

    const members = deskMemberCount ?? 0;

    // The default. No new row, no new link, no second copy of a person who is
    // already in the room — their existing permanent token now resolves this
    // batch too.
    if (!input.reviewerName?.trim()) {
      return {
        requestId: request.id,
        reviewerId: null,
        token: null,
        deskMemberCount: members,
      };
    }

    const token = generateShareToken();
    const { data: reviewer, error: reviewerError } = await supabase
      .from('approval_reviewers')
      .insert({
        request_id: request.id,
        owner_id: ownerId,
        workspace_id: input.workspaceId,
        name: input.reviewerName.trim(),
        email: input.reviewerEmail?.trim() || null,
        token_hash: await hashShareToken(token),
        expires_at: defaultExpiry().toISOString(),
      })
      .select('id')
      .single();
    if (reviewerError) throw new Error(reviewerError.message);

    return {
      requestId: request.id,
      reviewerId: reviewer.id,
      token,
      deskMemberCount: members,
    };
  }

  /**
   * The URL of a photo the client sent back. Scoped to media that came through
   * a review, so this can never be used to read an arbitrary asset by id.
   */
  async getClientPhotoUrl(mediaId: string): Promise<string | null> {
    const { data, error } = await supabase
      .from('media')
      .select('url')
      .eq('id', mediaId)
      .eq('source', 'client_review')
      .maybeSingle();
    if (error) throw new Error(error.message);
    return data?.url ?? null;
  }

  /** Kills a link immediately. The row is kept so the audit trail survives. */
  async revokeReviewer(reviewerId: string): Promise<void> {
    const { error } = await supabase
      .from('approval_reviewers')
      .update({ revoked_at: new Date().toISOString() })
      .eq('id', reviewerId);
    if (error) throw new Error(error.message);
  }

  /**
   * A fresh token for the same reviewer. Used when a link was sent to the
   * wrong place or has expired; the old hash is overwritten, so the old link
   * stops working in the same statement that starts the new one.
   */
  async reissueLink(reviewerId: string): Promise<string> {
    const token = generateShareToken();
    const { error } = await supabase
      .from('approval_reviewers')
      .update({
        token_hash: await hashShareToken(token),
        expires_at: defaultExpiry().toISOString(),
        revoked_at: null,
      })
      .eq('id', reviewerId);
    if (error) throw new Error(error.message);
    return token;
  }

  async closeRequest(requestId: string): Promise<void> {
    const { error } = await supabase
      .from('approval_requests')
      .update({ status: 'closed' })
      .eq('id', requestId);
    if (error) throw new Error(error.message);
  }
}

export const approvalService = new ApprovalService();
